import * as React from 'react';
import { isDataLoading, isDataError, isDataLoaded, makeLoadedData, Data } from '../types/data-providers';
import { useDispatch, useSelector } from 'react-redux';
import { useOrganizationData } from '../data/DataProviders/OrganizationDataProvider';
import { Thunks, Actions } from '../actions';
import { useCurrentAppRoute } from './app-route-hooks';
import { useHistory } from 'react-router-dom';
import { useSiteAlarmListData } from '../data/DataProviders/SiteAlarmListProvider';
import { useUserRoleOperationData } from '../data/DataProviders/RoleOperationDataProvider';
import { SelectedOrgInfo, SiteRoleData, UserOperations, UserRole } from '../types';
import { flattenRoleLocationList } from '../lib/user-helpers';
import { useLocationListData } from '../data/DataProviders/LocationListDataProvider';
import { Selectors } from '../selectors';

export enum RecordDataState {
    Loading = 'Loading',
    Found = 'Found',
    NotFound = 'NotFound',
    None = 'None',
    Error = 'Error',
}

export type RecordData<T = unknown> =
    | {
          state: RecordDataState.Loading;
      }
    | {
          state: RecordDataState.Found;
          value: T;
      }
    | {
          state: RecordDataState.NotFound;
      }
    | {
          state: RecordDataState.None;
      }
    | { state: RecordDataState.Error; errorMessage: string };

export const useSelectedOrganizationData = (): RecordData<{ id: string; name: string; address: string }> => {
    const organizationData = useOrganizationData();

    if (isDataLoading(organizationData)) {
        return { state: RecordDataState.Loading };
    }

    if (isDataError(organizationData)) {
        return { state: RecordDataState.Error, errorMessage: organizationData.errorMessage };
    }

    return {
        state: RecordDataState.Found,
        value: {
            id: organizationData.value.id,
            name: organizationData.value.name,
            address: organizationData.value.address,
        },
    };
};

/**this hook used when location need to refresg after create or delete */
export const useRefreshLocationList = (): Data<SelectedOrgInfo> => {
    const locationList = useLocationListData();
    const isLocationRefresh = useSelector(Selectors.Dashboard.isRefresh);
    const locationListData =
        isLocationRefresh && locationList.length > 1 ? locationList[locationList.length - 1] : locationList[0];
    return locationListData;
};

export const useSelectedLocationData = (): RecordData<{
    id: string;
    name: string;
    address: string | null;
}> => {
    const locationData = useRefreshLocationList();
    const currentAppRoute = useCurrentAppRoute();

    if (isDataLoading(locationData)) {
        return { state: RecordDataState.Loading };
    }

    if (isDataError(locationData)) {
        return { state: RecordDataState.Error, errorMessage: locationData.errorMessage };
    }

    if ('locationId' in currentAppRoute) {
        const location = locationData.value.locations.find((l) => l.locationId === currentAppRoute.locationId);
        if (location) {
            return {
                state: RecordDataState.Found,
                value: {
                    id: location.locationId,
                    name: location.locationName,
                    address: location.address,
                },
            };
        }

        return {
            state: RecordDataState.NotFound,
        };
    }

    return {
        state: RecordDataState.None,
    };
};

export const useSingleLocationRedirect = (): void => {
    const locationData = useRefreshLocationList();

    const dispatch = useDispatch();
    const history = useHistory();
    const currentRoute = useCurrentAppRoute();

    React.useEffect(() => {
        if (isDataLoaded(locationData) && locationData.value.locations.length === 1) {
            dispatch(
                Thunks.Dashboard.singleLocationFound(history, currentRoute, {
                    locationId: locationData.value.locations[0].locationId,
                })
            );
        } else {
            dispatch(Actions.Dashboard.autoRedirectBannerDismissed());
        }
    });
};

export const useSelectedSiteData = (): RecordData<{
    id: string;
    name: string;
    alarmCount?: number | undefined;
}> => {
    //const locationData = useLocationData();
    const siteListData = useSiteAlarmListData();
    const currentAppRoute = useCurrentAppRoute();

    if (isDataLoading(siteListData)) {
        return { state: RecordDataState.Loading };
    }

    if (isDataError(siteListData)) {
        return { state: RecordDataState.Error, errorMessage: siteListData.errorMessage };
    }

    if ('siteId' in currentAppRoute) {
        const site = (siteListData.value || []).find((l) => l.siteId === currentAppRoute.siteId);
        if (site) {
            return {
                state: RecordDataState.Found,
                value: {
                    id: site.siteId,
                    name: site.siteName,
                    alarmCount: site.alarmCount,
                },
            };
        }

        return {
            state: RecordDataState.NotFound,
        };
    }

    return {
        state: RecordDataState.None,
    };
};

export const useUserRoles = (): RecordData<SiteRoleData[]> => {
    const roleInfo = useUserRoleOperationData();
    const locationsList = useLocationListData();
    const locationListData = locationsList[0];

    const siteRoleArray: SiteRoleData[] = [];

    if (isDataLoading(roleInfo) || isDataLoading(locationListData)) {
        return { state: RecordDataState.Loading };
    }

    if (isDataError(roleInfo)) {
        return { state: RecordDataState.Error, errorMessage: roleInfo.errorMessage };
    }
    if (isDataError(locationListData)) {
        return { state: RecordDataState.Error, errorMessage: locationListData.errorMessage };
    }
    if (isDataLoaded(roleInfo) && isDataLoaded(locationListData)) {
        const roleData = flattenRoleLocationList(roleInfo.value);
        const hasEatonAdminRole = roleData.find((roles) => roles.roleName === UserRole.EatonAdmin);
        if (hasEatonAdminRole) {
            const eatonAdminData = {
                roleId: hasEatonAdminRole.roleId,
                roleName: hasEatonAdminRole.roleName,
                operations: hasEatonAdminRole.operation || [],
                siteId: locationListData.value.organizationId,
                siteName: 'All Location',
                siteAddress: '',
            };
            siteRoleArray.push(eatonAdminData);
        }

        const hasOrgAdminRole = roleData.find(
            (roles) => roles.roleName === UserRole.OrgAdmin && roles.siteId === locationListData.value.organizationId
        );
        if (!hasEatonAdminRole && hasOrgAdminRole) {
            const OrgAdminData = {
                roleId: hasOrgAdminRole.roleId,
                roleName: UserRole.OrgAdmin,
                operations: hasOrgAdminRole.operation || [],
                siteId: locationListData.value.organizationId,
                siteName: locationListData.value.organizationName,
                siteAddress: locationListData.value.address || '',
            };
            siteRoleArray.push(OrgAdminData);
        }
        if (!hasEatonAdminRole && !hasOrgAdminRole) {
            locationListData.value.locations.forEach((loc) => {
                roleData.forEach((role) => {
                    if (role.siteId === loc.locationId) {
                        const val = {
                            roleId: role.roleId,
                            roleName: role.roleName,
                            operations: role.operation || [],
                            siteId: loc.locationId,
                            siteName: loc.locationName,
                            siteAddress: loc.address || '',
                        };
                        siteRoleArray.push(val);
                    }
                });
            });
        }
        if (process.env.NODE_ENV === 'test') {
            makeLoadedData(roleData);
            const testData = {
                roleId: '',
                roleName: UserRole.OrgAdmin.toString(),
                operations: [UserOperations.UserManagement, UserOperations.SiteManagement, UserOperations.DeleteUser],
                siteId: locationListData.value.organizationId,
                siteName: locationListData.value.organizationName,
                siteAddress: locationListData.value.address,
            };
            siteRoleArray.push(testData);
        }
        return {
            state: RecordDataState.Found,
            value: siteRoleArray,
        };
    }
    return {
        state: RecordDataState.NotFound,
    };
};
export const isRecordFound = <T>(
    data: RecordData<T>
): data is Extract<RecordData<T>, { state: RecordDataState.Found }> => data.state === RecordDataState.Found;

export const isRecordLoading = <T>(
    data: RecordData<T>
): data is Extract<RecordData<T>, { state: RecordDataState.Loading }> => data.state === RecordDataState.Loading;

export const isRecordError = <T>(
    data: RecordData<T>
): data is Extract<RecordData<T>, { state: RecordDataState.Error }> => data.state === RecordDataState.Error;

export const isRecordNotFound = <T>(
    data: RecordData<T>
): data is Extract<RecordData<T>, { state: RecordDataState.NotFound }> => data.state === RecordDataState.NotFound;

export const isRecordNone = <T>(data: RecordData<T>): data is Extract<RecordData<T>, { state: RecordDataState.None }> =>
    data.state === RecordDataState.None;
